// src/components/auth/RegisterModal.tsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuthContext } from '../../services/AuthProvider';
import { useTranslation } from '../../useTranslation';
import { PasswordField } from './PasswordField';
import { AuthCardHeader } from './AuthCardHeader';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  hideCancel?: boolean;
  inline?: boolean;
  language: string;
  onLanguageChange?: (language: string) => void;
  theme?: 'light' | 'dark';
  onThemeChange?: (theme: 'light' | 'dark') => void;
}

const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  hideCancel,
  inline,
  language,
  onLanguageChange,
  theme,
  onThemeChange,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { register } = useAuthContext();
  const { t } = useTranslation(language);

  if (!isOpen) return null;

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (password !== confirmPassword) {
      setError(t.passwordsDoNotMatch || 'Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError(t.passwordTooShort || 'Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      const result = await register(email, password);
      if (!result.success) {
        setError(result.error || 'Registration failed');
      } else {
        setSuccess(t.registerCheckEmail);
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  const formCard = (
    <div
      className="p-5 sm:p-6 md:p-8 w-full max-w-md mx-auto relative overflow-hidden rounded-2xl border shadow-xl backdrop-blur-xl bg-white/95 dark:bg-slate-800/95 border-stone-200/80 dark:border-slate-600/80 text-stone-900 dark:text-slate-100"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-400 rounded-t-2xl" />

      <AuthCardHeader
        language={language}
        onLanguageChange={onLanguageChange}
        theme={theme}
        onThemeChange={onThemeChange}
      />

      <h2 className="text-xl sm:text-2xl font-semibold text-stone-900 dark:text-white text-center">
        {t.register || 'Create Account'}
      </h2>
      <p className="text-stone-500 dark:text-slate-400 text-center mt-1 mb-6 text-sm">
        {t.registerSubtitle}
      </p>

      {error && (
        <div className="bg-red-50/90 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl mb-4 text-sm flex items-center gap-2 backdrop-blur">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-emerald-50/90 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 px-4 py-3 rounded-xl mb-4 text-sm text-pretty">
          {success}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="mt-2 block w-full text-center font-semibold text-emerald-700 dark:text-emerald-300 underline"
          >
            {t.login || 'Sign in'}
          </button>
        </div>
      )}

      <form onSubmit={handleRegister} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-stone-700 dark:text-slate-300 mb-1">
            {t.email || 'Email'}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 dark:border-slate-500 bg-white/80 dark:bg-slate-700/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400"
            placeholder="name@example.com"
            required
            autoComplete="email"
          />
        </div>

        <PasswordField
          value={password}
          onChange={setPassword}
          label={t.password || 'Password'}
          required
          autoComplete="new-password"
        />

        <PasswordField
          value={confirmPassword}
          onChange={setConfirmPassword}
          label={t.confirmPassword || 'Confirm Password'}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          id="confirm-password"
        />

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors min-h-[48px]"
        >
          {isLoading ? (
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : null}
          {t.createAccount || 'Create Account'}
        </button>
      </form>

      <div className="mt-6 text-center space-y-3">
        <p className="text-sm text-stone-600 dark:text-slate-400">
          {t.alreadyHaveAccount ?? 'Already have an account?'}{' '}
          <button type="button" onClick={onSwitchToLogin} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
            {t.login ?? 'Sign In'}
          </button>
        </p>
        {!hideCancel && (
          <button type="button" onClick={onClose} className="text-sm text-stone-400 hover:text-stone-600 dark:hover:text-slate-300">
            {t.cancel ?? 'Cancel'}
          </button>
        )}
      </div>
    </div>
  );

  if (inline) {
    return isOpen ? formCard : null;
  }
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-stone-900/40 dark:bg-black/50 backdrop-blur-md flex items-center justify-center z-[9999] p-4 sm:p-6 safe-area-padding">
      {formCard}
    </div>,
    document.body
  );
};

export default RegisterModal;
